// useCarga.ts — carga de un dato del panel con estados «cargando» y «error» (round 2026-10-30, etapa B3).
//
// Los servicios del panel devuelven `null` cuando algo falla (apiFetch nunca lanza). Aquí `null` es SIEMPRE «no se pudo
// leer»: el dato queda en `null` y `error` en true. No se conserva el valor anterior a propósito: un modo real que no se
// pudo volver a leer no debe seguir mostrándose como si se supiera, y un formulario no debe quedar activo sobre datos viejos.
//
// Excepción, pedida: la lectura PERIÓDICA de fondo (`silenciosa`). Si esa falla y ya había un dato, no se borra la pantalla
// cada 45 segundos por un tropiezo de la red: el dato se conserva y `desactualizado` pasa a true, para que la pantalla diga
// «puede haber cambiado» y bloquee lo delicado hasta la próxima lectura buena. Una lectura que la persona pide (abrir,
// «Actualizar», «Reintentar») nunca es silenciosa: si falla, es un error a la vista.
//
// Si llegan dos respuestas en desorden (se pidió dos veces seguidas), vale la última que se PIDIÓ, no la última que llegó.
import { useCallback, useEffect, useRef, useState } from "react";

export interface OpcionesRecarga {
  /** Lectura de fondo: no enciende «cargando» y, si falla con un dato ya leído, lo conserva marcándolo como desactualizado. */
  silenciosa?: boolean;
}

export interface Carga<T> {
  datos: T | null;
  /** true mientras hay una lectura en curso (también en las recargas: la pantalla decide si lo muestra). */
  cargando: boolean;
  /** La última lectura falló y no hay dato. */
  error: boolean;
  /** La última lectura de fondo falló y se está mostrando el dato anterior, que puede haber cambiado. */
  desactualizado: boolean;
  /** Vuelve a leer. Devuelve lo leído (o null si falló). */
  recargar: (opciones?: OpcionesRecarga) => Promise<T | null>;
}

export function useCarga<T>(cargador: () => Promise<T | null>): Carga<T> {
  const [datos, setDatos] = useState<T | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(false);
  const [desactualizado, setDesactualizado] = useState(false);
  const cargadorRef = useRef(cargador);
  cargadorRef.current = cargador;
  const secuencia = useRef(0);
  const montado = useRef(true);
  /** ¿Hay un dato leído? (para decidir, desde un callback estable, si una lectura de fondo fallida puede conservarlo). */
  const hayDato = useRef(false);

  const recargar = useCallback(async (opciones?: OpcionesRecarga): Promise<T | null> => {
    const silenciosa = opciones?.silenciosa === true;
    const mia = ++secuencia.current;
    if (!silenciosa) setCargando(true);
    let leido: T | null = null;
    try {
      leido = await cargadorRef.current();
    } catch {
      leido = null;
    }
    if (!montado.current || mia !== secuencia.current) return leido;
    if (silenciosa && leido === null && hayDato.current) {
      setDesactualizado(true);
      setCargando(false);
      return null;
    }
    hayDato.current = leido !== null;
    setDatos(leido);
    setError(leido === null);
    setDesactualizado(false);
    setCargando(false);
    return leido;
  }, []);

  useEffect(() => {
    montado.current = true;
    void recargar();
    return () => {
      montado.current = false;
    };
  }, [recargar]);

  return { datos, cargando, error, desactualizado, recargar };
}
